"use client";

import { useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<"parquet" | "synthetic">("parquet");

  const parquetData = [
    { timeKey: "pricingPage.table.luVi712", regular: 40000, vip10: 36000, vip15: 34000 },
    { timeKey: "pricingPage.table.luVi1218", regular: 58000, vip10: 52200, vip15: 49300 },
    { timeKey: "pricingPage.table.luVi1801", regular: 72000, vip10: 64800, vip15: 61200 },
    { timeKey: "pricingPage.table.sab712", regular: 40000, vip10: 36000, vip15: 34000 },
    { timeKey: "pricingPage.table.sab1201", regular: 72000, vip10: 64800, vip15: 61200 },
    { timeKey: "pricingPage.table.dom712", regular: 40000, vip10: 36000, vip15: 34000, isWeekendHighlight: true },
    { timeKey: "pricingPage.table.dom1201", regular: 72000, vip10: 64800, vip15: 61200, isWeekendHighlight: true },
  ];

  const syntheticData = [
    { timeKey: "pricingPage.table.luVi712", regular: 26000, vip10: 23400, vip15: 22100 },
    { timeKey: "pricingPage.table.luVi1218", regular: 38000, vip10: 34200, vip15: 32300 },
    { timeKey: "pricingPage.table.luVi1801", regular: 47000, vip10: 42300, vip15: 39950 },
    { timeKey: "pricingPage.table.sab712", regular: 26000, vip10: 23400, vip15: 22100 },
    { timeKey: "pricingPage.table.sab1201", regular: 47000, vip10: 42300, vip15: 39950 },
    { timeKey: "pricingPage.table.dom712", regular: 26000, vip10: 23400, vip15: 22100, isWeekendHighlight: true },
    { timeKey: "pricingPage.table.dom1201", regular: 47000, vip10: 42300, vip15: 39950, isWeekendHighlight: true },
  ];

  const currentData = activeTab === "parquet" ? parquetData : syntheticData;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(i18n.language?.startsWith("es") ? "es-AR" : "en-US", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="bg-surface-muted min-h-screen pb-24">
      {/* Hero */}
      <section className="relative flex min-h-[220px] flex-col items-center justify-center overflow-hidden px-4 sm:min-h-[280px] md:min-h-[300px]">
        <div
          className="absolute inset-0 bg-slate-950"
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-[0.45]"
          style={{
            backgroundImage: "url('/new/DSC08873.JPG')",
            backgroundPosition: "center 40%",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_90%_80%_at_50%_20%,transparent_0%,rgba(15,23,42,0.92)_65%,rgb(15,23,42)_100%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-linear-to-b from-slate-950/30 via-slate-950/70 to-slate-950"
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-3xl px-2 pt-6 text-center sm:pt-8">
          <p className="mx-auto mb-5 inline-flex max-w-2xl items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium leading-snug text-white/95 shadow-sm backdrop-blur-md sm:text-sm">
            {t("pricingPage.subtitle")}
          </p>
          <h1 className="text-[1.75rem] font-semibold tracking-[-0.02em] text-white sm:text-4xl md:text-5xl">
            {t("pricingPage.title")}
          </h1>
          <nav
            className="mt-5 flex items-center justify-center gap-2 text-[13px] font-medium text-slate-400"
            aria-label="Breadcrumb"
          >
            <Link
              href="/"
              className="transition-colors hover:text-white"
            >
              {t("header.home")}
            </Link>
            <span className="text-slate-600" aria-hidden>
              —
            </span>
            <span className="text-slate-200">{t("header.pricing")}</span>
          </nav>
        </div>
      </section>

      <div className="relative z-20 mx-auto -mt-9 w-full max-w-5xl px-4 sm:-mt-10 sm:px-6">
        {/* Surface switcher — extra space below before the card */}
        <div className="mx-auto mb-12 max-w-lg">
          <div className="flex rounded-full border border-slate-200/90 bg-white p-1 shadow-[0_4px_24px_-4px_rgba(15,23,42,0.1)] ring-1 ring-slate-900/5">
            <button
              type="button"
              onClick={() => setActiveTab("parquet")}
              className={`flex-1 rounded-full py-3.5 px-3 text-center text-[13px] font-semibold transition-all duration-200 sm:px-4 sm:text-sm ${
                activeTab === "parquet"
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              }`}
            >
              {t("pricingPage.tabFutsal5")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("synthetic")}
              className={`flex-1 rounded-full py-3.5 px-3 text-center text-[13px] font-semibold transition-all duration-200 sm:px-4 sm:text-sm ${
                activeTab === "synthetic"
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              }`}
            >
              {t("pricingPage.tabFutsal3")}
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.12)]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr>
                  <th className="bg-slate-900 px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.07em] text-white sm:px-6 sm:py-5">
                    {t("pricingPage.table.time")}
                  </th>
                  <th className="bg-slate-900 px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-200 sm:px-5 sm:py-5">
                    {t("pricingPage.table.regular")}
                  </th>
                  <th className="bg-slate-800 px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-100 sm:px-5 sm:py-5">
                    {t("pricingPage.table.vip10")}
                  </th>
                  <th className="bg-slate-800 px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-100 sm:px-5 sm:py-5">
                    {t("pricingPage.table.vip15")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((row) => (
                  <tr
                    key={`${activeTab}-${row.timeKey}`}
                    className={`border-b border-slate-100 transition-colors last:border-0 ${
                      row.isWeekendHighlight
                        ? "bg-amber-50/50 hover:bg-amber-50/70"
                        : "hover:bg-slate-50/90 even:bg-slate-50/35"
                    }`}
                  >
                    <td
                      className={`px-5 py-4 text-[15px] font-medium leading-snug sm:px-6 sm:py-4.5 ${
                        row.isWeekendHighlight
                          ? "border-l-[3px] border-amber-400 text-slate-900"
                          : "text-slate-700"
                      }`}
                    >
                      {t(row.timeKey)}
                    </td>
                    <td className="border-l border-slate-100 px-4 py-4 text-center text-[15px] font-semibold tabular-nums text-slate-900 sm:text-base">
                      {formatPrice(row.regular)}
                    </td>
                    <td className="border-l border-slate-100 bg-primary-soft/45 px-4 py-4 text-center text-[15px] font-semibold tabular-nums text-primary-strong sm:text-base">
                      {formatPrice(row.vip10)}
                    </td>
                    <td className="border-l border-slate-100 bg-slate-100/90 px-4 py-4 text-center text-[15px] font-semibold tabular-nums text-slate-800 sm:text-base">
                      {formatPrice(row.vip15)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-px border-t border-slate-100 bg-slate-100 md:grid-cols-2">
            <div className="bg-slate-50/60 p-6 sm:p-7">
              <div className="mb-2 flex gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-200/80">
                  <Info className="h-4 w-4 text-primary" strokeWidth={2} />
                </span>
                <h4 className="pt-0.5 text-[15px] font-semibold text-slate-900">
                  {t("pricingPage.table.vip10")}
                </h4>
              </div>
              <p className="text-sm leading-relaxed text-slate-600 sm:pl-12">
                {t("pricingPage.table.vip10Desc")}
              </p>
            </div>
            <div className="bg-white p-6 sm:p-7">
              <div className="mb-2 flex gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200/80">
                  <Info className="h-4 w-4 text-slate-600" strokeWidth={2} />
                </span>
                <h4 className="pt-0.5 text-[15px] font-semibold text-slate-900">
                  {t("pricingPage.table.vip15")}
                </h4>
              </div>
              <p className="text-sm leading-relaxed text-slate-600 sm:pl-12">
                {t("pricingPage.table.vip15Desc")}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 text-center">
          <Link
            href="/"
            className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-primary px-8 py-3 text-base font-semibold text-white shadow-lg transition hover:bg-primary-strong hover:shadow-xl active:scale-[0.99]"
          >
            {t("header.bookSlot")}
          </Link>
        </div>
      </div>
    </div>
  );
}
